/**
 * ── Highlight palette: SINGLE SOURCE ─────────────────────────────────────────
 *
 * These are the **10 pastel** colors that the app published on Google Play (2022) seeded into
 * the `color` table of `android/app/src/main/assets/mydata/highlight.mybible` and
 * served through `HighlightApi.getColors()`.
 *
 * Why they matter: the 2.0 update is **in place** (same
 * `applicationId`, real users). The migrator copies the user's hex values
 * verbatim; if the new app offered only 4 colors, we would have to **collapse 10 → 4**
 * and a user with 6 distinct colors would irreversibly lose information.
 * With this palette, the mapping is the **identity**.
 *
 * The pastels also support **solid backgrounds**: with the light-theme ink they
 * reach 11–19:1, while the saturated Tailwind colors used by the new app
 * were only 4.3:1 and required painting them at 12% alpha.
 *
 * `key` is a stable identifier (for the UI's i18n labels, which
 * live in the web layer); `id` is the legacy table ID, useful for tracing.
 */
import { DARK_BASE_TOKENS, LIGHT_BASE_TOKENS } from "./theme-tokens";

export interface HighlightPaletteColor {
  /** Legacy `color` table ID (1..10). For tracing only. */
  id: number;
  /** Stable key for the translatable label. */
  key: string;
  /** Lowercase `#rrggbb` hex. */
  hex: string;
}

export const HIGHLIGHT_PALETTE: readonly HighlightPaletteColor[] = [
  { id: 1, key: "pink", hex: "#ffc8c8" },
  { id: 2, key: "peach", hex: "#ffd9a8" },
  { id: 3, key: "yellow", hex: "#fff8a8" },
  { id: 4, key: "lime", hex: "#d8ffa8" },
  { id: 5, key: "green", hex: "#baffba" },
  { id: 6, key: "cyan", hex: "#c9f9ff" },
  { id: 7, key: "lightBlue", hex: "#c9d8ff" },
  { id: 8, key: "periwinkle", hex: "#d0c9ff" },
  { id: 9, key: "lilac", hex: "#e0c9ff" },
  { id: 10, key: "magenta", hex: "#fcc9ff" },
] as const;

/**
 * Maximum number of swatches the highlight sheet shows at once
 * (`MAX_NUM_COLOR_OPTIONS` of the legacy). With user-defined colors the list
 * can grow; it is trimmed here.
 */
export const MAX_HIGHLIGHT_SWATCHES = 10;

/** Pastel yellow from the palette: also the search-match highlight. */
export const SEARCH_MATCH_COLOR = "#fff8a8";

/**
 * Normalizes a color hex to lowercase `#rrggbb`. Accepts the **no-hash**
 * form used by the legacy DB (`SelectedVersesOptions` stored
 * `ffc8c8`) and the short `#abc` form. Returns `null` if it is not a valid color.
 */
export function normalizeHexColor(value: string | null | undefined): string | null {
  if (typeof value !== "string") {
    return null;
  }
  const raw = value.trim().replace(/^#/, "").toLowerCase();
  if (/^[0-9a-f]{3}$/.test(raw)) {
    const [r, g, b] = raw;
    return `#${r ?? ""}${r ?? ""}${g ?? ""}${g ?? ""}${b ?? ""}${b ?? ""}`;
  }
  if (/^[0-9a-f]{6}$/.test(raw)) {
    return `#${raw}`;
  }
  return null;
}

const RELATIVE_LUMINANCE_RED = 0.2126;
const RELATIVE_LUMINANCE_GREEN = 0.7152;
const RELATIVE_LUMINANCE_BLUE = 0.0722;
const SRGB_MAX = 255;
const SRGB_THRESHOLD = 0.039_28;
const SRGB_LINEAR_DIVISOR = 12.92;
const SRGB_OFFSET = 0.055;
const SRGB_SCALE = 1.055;
const SRGB_GAMMA = 2.4;
const HEX_RADIX = 16;
const HEX_PAIR = 2;

function channelToLinear(channel8: number): number {
  const c = channel8 / SRGB_MAX;
  return c <= SRGB_THRESHOLD
    ? c / SRGB_LINEAR_DIVISOR
    : ((c + SRGB_OFFSET) / SRGB_SCALE) ** SRGB_GAMMA;
}

/** Luminancia relativa WCAG de un hex (0 = negro, 1 = blanco). */
export function relativeLuminance(hex: string): number {
  const normalized = normalizeHexColor(hex);
  if (!normalized) {
    return 1;
  }
  const body = normalized.slice(1);
  const r = Number.parseInt(body.slice(0, HEX_PAIR), HEX_RADIX);
  const g = Number.parseInt(body.slice(HEX_PAIR, HEX_PAIR * 2), HEX_RADIX);
  const b = Number.parseInt(body.slice(HEX_PAIR * 2), HEX_RADIX);
  return (
    RELATIVE_LUMINANCE_RED * channelToLinear(r) +
    RELATIVE_LUMINANCE_GREEN * channelToLinear(g) +
    RELATIVE_LUMINANCE_BLUE * channelToLinear(b)
  );
}

/** Luminance threshold below which a background is considered dark. */
const DARK_BACKGROUND_LUMINANCE = 0.4;

/**
 * Equivalent to legacy `Colors.isDark`: decides whether that background needs
 * light text. All 10 pastels are light → dark ink, just as in
 * the screenshots (also in dark mode: legacy keeps the pastel and puts
 * texto negro encima).
 */
export function isDarkBackground(hex: string): boolean {
  return relativeLuminance(hex) < DARK_BACKGROUND_LUMINANCE;
}

/** Readable ink over a solid highlight background. */
export function highlightTextColor(hex: string): string {
  return isDarkBackground(hex) ? "#f7f2e8" : "#1a0f0d";
}

/**
 * Text colors for BIBLE TEXT over a solid highlight background.
 *
 * The 10 pastels are light **in dark mode too** (legacy kept the
 * pastel and put black ink on top). Therefore, inside a highlighted block the
 * Strong number and Jesus' words cannot use the active-mode variant —
 * light blue `#a9c6e8` on pastel yellow is illegible. They are chosen by the
 * **luminance of the actual background**, not by the mode.
 *
 * Values come from the base tokens, not loose literals: an identity change
 * is still made in one place.
 */
export function readingInkOn(backgroundHex: string): {
  text: string;
  strong: string;
  redLetter: string;
} {
  const dark = isDarkBackground(backgroundHex);
  const tokens = dark ? DARK_BASE_TOKENS : LIGHT_BASE_TOKENS;
  return {
    text: highlightTextColor(backgroundHex),
    strong: tokens.strong,
    redLetter: tokens.redLetter,
  };
}

export interface OrderHighlightPaletteInput {
  /** Colors applied to the currently selected verses. */
  selectedColors?: readonly string[];
  /** Colors present in the visible chapter. */
  chapterColors?: readonly string[];
  /** User colors sorted by descending usage frequency. */
  usedColors?: readonly string[];
  /** Base palette (by default, the 10 legacy pastels). */
  palette?: readonly string[];
  /** Tope de swatches devueltos. */
  max?: number;
}

/**
 * Highlight-sheet order, **exactly as in legacy**
 * (`SelectedVersesOptions.tsx` `mergeColors`):
 *
 *   selection colors → chapter colors → used by frequency →
 *   the rest of the palette
 *
 * deduplicated and trimmed to `max`. The reason it exists: people who highlight
 * usually reuse the same two or three colors; putting them first prevents a
 * newly used color from falling out of the visible row.
 */
export function orderHighlightPalette(input: OrderHighlightPaletteInput = {}): string[] {
  const max = input.max ?? MAX_HIGHLIGHT_SWATCHES;
  const base = input.palette ?? HIGHLIGHT_PALETTE.map((c) => c.hex);
  const ordered: string[] = [];
  const seen = new Set<string>();

  const push = (raw: string | null | undefined): void => {
    const hex = normalizeHexColor(raw);
    if (!hex || seen.has(hex)) {
      return;
    }
    seen.add(hex);
    ordered.push(hex);
  };

  for (const c of input.selectedColors ?? []) {
    push(c);
  }
  for (const c of input.chapterColors ?? []) {
    push(c);
  }
  for (const c of input.usedColors ?? []) {
    push(c);
  }
  for (const c of base) {
    push(c);
  }

  return ordered.slice(0, max);
}
